import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, Heart, Mic, Shield, MessageCircle, Phone, ChevronRight, BookOpen, Brain, Smartphone, Sparkles, Wind, Smile, TrendingUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AvatarDemo } from './AvatarDemo';

const LandingPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [breatheScale, setBreatheScale] = useState(1);
  const { user } = useAuth();
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const breatheInterval = setInterval(() => {
      setBreatheScale(prev => prev === 1 ? 1.1 : 1);
    }, 3000);
    return () => clearInterval(breatheInterval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-stone-50 to-teal-50">
      {/* Header */}
      <header className="relative bg-white/80 backdrop-blur-md shadow-sm border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-500 rounded-xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-teal-600 to-orange-500 bg-clip-text text-transparent">
                Mira
              </span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#home" className="text-slate-700 hover:text-teal-600 transition-colors duration-200">Home</a>
              <a href="#features" className="text-slate-700 hover:text-teal-600 transition-colors duration-200">Features</a>
              <a href="#journal" className="text-slate-700 hover:text-teal-600 transition-colors duration-200">Journal</a>
              <a href="#avatar" className="text-slate-700 hover:text-teal-600 transition-colors duration-200">Avatar</a>
              <a href="#crisis" className="text-red-600 hover:text-red-700 font-medium transition-colors duration-200">Crisis Support</a>
              <button 
                onClick={() => navigate('/about-us')}
                className="text-slate-700 hover:text-teal-600 transition-colors duration-200"
              >
                About Us
              </button>
              <button
                onClick={() => navigate('/how-it-works')}
                className="text-slate-700 hover:text-teal-600 transition-colors duration-200"
              >
                How It Works
              </button>
              <button 
                onClick={() => navigate('/auth')}
                className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 py-2 rounded-full hover:from-teal-600 hover:to-teal-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Get Started
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className="md:hidden p-2 rounded-lg hover:bg-orange-100 transition-colors duration-200"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-orange-100">
              <nav className="flex flex-col space-y-4">
                <a href="#home" className="text-slate-700 hover:text-teal-600 transition-colors duration-200">Home</a>
                <a href="#features" className="text-slate-700 hover:text-teal-600 transition-colors duration-200">Features</a>
                <a href="#journal" className="text-slate-700 hover:text-teal-600 transition-colors duration-200">Journal</a>
                <a href="#avatar" className="text-slate-700 hover:text-teal-600 transition-colors duration-200">Avatar</a>
                <a href="#crisis" className="text-red-600 hover:text-red-700 font-medium transition-colors duration-200">Crisis Support</a>
                <button 
                  onClick={() => navigate('/about-us')}
                  className="text-slate-700 hover:text-teal-600 transition-colors duration-200 text-left"
                >
                  About Us
                </button>
                <button
                  onClick={() => navigate('/how-it-works')}
                  className="text-slate-700 hover:text-teal-600 transition-colors duration-200 text-left"
                >
                  How It Works
                </button>
                <button 
                  onClick={() => navigate('/auth')}
                  className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 py-2 rounded-full hover:from-teal-600 hover:to-teal-700 transition-all duration-200 shadow-md w-fit"
                >
                  Get Started
                </button>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative py-20 lg:py-32 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
          <div className="absolute top-40 right-20 w-96 h-96 bg-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute -bottom-20 left-1/3 w-80 h-80 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="text-center lg:text-left animate-in">
              <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full mb-6 border border-teal-200">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">Your Path to Inner Peace</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
                Find calm in the{' '}
                <span className="bg-gradient-to-r from-teal-600 to-orange-500 bg-clip-text text-transparent">
                  chaos
                </span>
                <br />
                Embrace{' '}
                <span className="bg-gradient-to-r from-orange-500 to-teal-600 bg-clip-text text-transparent">
                  peace
                </span>{' '}
                within
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Experience compassionate AI support tailored to your unique mental health journey.
                Mira provides 24/7 empathetic conversations, voice journaling, and personalized wellness guidance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={() => navigate('/auth')}
                  className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-teal-600 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Start Your Journey
                </button>
                <button
                  onClick={() => navigate('/how-it-works')}
                  className="border-2 border-teal-500 text-teal-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-teal-50 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <BookOpen className="w-5 h-5" />
                  How It Works
                </button>
              </div>
            </div>

            {/* Hero Visual */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative w-full max-w-lg">
                {/* Soft gradient glow behind image */}
                <div
                  className="absolute inset-0 bg-gradient-to-br from-teal-300/40 to-orange-300/40 rounded-3xl blur-3xl transition-transform duration-3000 ease-in-out"
                  style={{ transform: `scale(${breatheScale})` }}
                ></div>

                {/* Hero Image */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-slate-200/50">
                  <img
                    src="https://images.pexels.com/photos/936090/pexels-photo-936090.jpeg?auto=compress&cs=tinysrgb&w=800"
                    alt="Smiling man outdoors in natural light"
                    className="w-full h-auto object-cover"
                  />

                  {/* Subtle gradient overlay for depth */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/5 via-transparent to-transparent"></div>
                </div>

                {/* Floating badges around the image */}
                <div className="absolute -bottom-4 left-4 sm:-left-4 bg-white rounded-2xl px-4 sm:px-6 py-2 sm:py-3 shadow-xl border border-teal-100 animate-bounce" style={{ animationDuration: '3s' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs sm:text-sm font-semibold text-slate-700">24/7 Support</span>
                  </div>
                </div>

                <div className="absolute -top-4 right-4 sm:-right-4 bg-white rounded-2xl px-4 sm:px-6 py-2 sm:py-3 shadow-xl border border-orange-100 animate-bounce" style={{ animationDuration: '4s', animationDelay: '0.5s' }}>
                  <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-orange-500" />
                    <span className="text-xs sm:text-sm font-semibold text-slate-700">AI Empathy</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 pt-12 border-t border-teal-100">
            <div className="flex flex-wrap justify-center items-center gap-8 text-slate-500">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-teal-500" />
                <span className="text-sm">100% Private & Secure</span>
              </div>
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-orange-500" />
                <span className="text-sm">AI-Powered Empathy</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-teal-500" />
                <span className="text-sm">Proven Wellness Results</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white/50 relative overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-teal-100/30 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-orange-100/30 to-transparent rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 animate-in">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-50 to-orange-50 text-teal-700 px-4 py-2 rounded-full mb-4 border border-teal-100">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">Wellness Features</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Compassionate AI Support
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Discover features designed to support your mental wellness journey with empathy and understanding.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* AI Avatar Companion */}
            <div className="bg-white rounded-3xl p-8 shadow-card hover:shadow-card-hover border border-orange-100 ring-1 ring-teal-200/50 group cursor-pointer transition-all duration-300 hover:-translate-y-2 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-teal-100/50 to-transparent rounded-full -mr-16 -mt-16"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-200 shadow-lg">
                  <Video className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">AI Avatar Companion</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Meet your personalized AI companion powered by Tavus technology. Experience face-to-face conversations that feel natural and supportive.
                </p>
                <button
                  onClick={() => navigate('/auth')}
                  className="text-teal-600 font-semibold flex items-center gap-2 hover:gap-3 transition-all duration-200"
                >
                  Sign Up to Try <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Voice Journaling */}
            <div className="bg-white rounded-3xl p-8 shadow-card hover:shadow-card-hover border border-orange-100 ring-1 ring-orange-200/50 group cursor-pointer transition-all duration-300 hover:-translate-y-2 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-100/50 to-transparent rounded-full -mr-16 -mt-16"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-200 shadow-lg">
                  <Mic className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Voice Journaling</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Speak freely and let the app analyze your tone, pace, and vocal patterns. Our AI detects emotions, stress, and energy levels—offering insights to help you better understand and support your mental well-being
                </p>
                <button
                  onClick={() => navigate('/auth')}
                  className="text-orange-600 font-semibold flex items-center gap-2 hover:gap-3 transition-all duration-200"
                >
                  Sign Up to Try <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Secure Sessions */}
            <div className="bg-white rounded-3xl p-8 shadow-card hover:shadow-card-hover border border-teal-100 ring-1 ring-teal-200/50 group cursor-pointer transition-all duration-300 hover:-translate-y-2 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100/50 to-transparent rounded-full -mr-16 -mt-16"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-200 shadow-lg">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Secure & Private</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Your mental health journey is private. All conversations are encrypted end-to-end, ensuring your personal information remains completely confidential.
                </p>
                <button
                  onClick={() => navigate('/how-it-works')}
                  className="text-teal-600 font-semibold flex items-center gap-2 hover:gap-3 transition-all duration-200"
                >
                  Learn More <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wellness Journey Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-0 w-64 h-64 bg-teal-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float"></div>
          <div className="absolute bottom-1/4 right-0 w-64 h-64 bg-orange-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-50 to-orange-50 text-teal-700 px-4 py-2 rounded-full mb-4 border border-teal-200">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm font-medium">Your Journey to Peace</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              From Anxiety to Inner Peace
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Experience a gradual transformation as you work with Mira to build healthier mental wellness habits.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl p-8 border border-red-100 relative group hover:shadow-xl transition-all duration-300">
              <div className="absolute top-4 right-4 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-red-600 font-bold">1</div>
              <div className="mb-6 w-16 h-16 bg-gradient-to-br from-red-400 to-orange-400 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Wind className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Acknowledge</h3>
              <p className="text-slate-600 leading-relaxed">
                Recognize your feelings and anxieties without judgment. Mira provides a safe, supportive space to express yourself freely.
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-3xl p-8 border border-amber-100 relative group hover:shadow-xl transition-all duration-300">
              <div className="absolute top-4 right-4 w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 font-bold">2</div>
              <div className="mb-6 w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-400 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Understand</h3>
              <p className="text-slate-600 leading-relaxed">
                Gain insights into your emotional patterns through AI analysis. Learn what triggers stress and how to respond mindfully.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-green-50 rounded-3xl p-8 border border-teal-100 relative group hover:shadow-xl transition-all duration-300">
              <div className="absolute top-4 right-4 w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 font-bold">3</div>
              <div className="mb-6 w-16 h-16 bg-gradient-to-br from-teal-400 to-green-400 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Smile className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Transform</h3>
              <p className="text-slate-600 leading-relaxed">
                Build lasting peace through personalized practices. Develop coping strategies that work specifically for you.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-teal-50 via-blue-50 to-teal-50 rounded-3xl p-12 border border-teal-100 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-teal-200/30 to-transparent rounded-full -mr-48 -mt-48"></div>
            <div className="relative z-10 text-center max-w-3xl mx-auto">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-400 to-blue-400 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl">
                <Heart className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                Your mental wellness is a journey, not a destination
              </h3>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Every conversation, every journal entry, every moment of reflection brings you closer to the peace you deserve. Mira is here for every step of your journey.
              </p>
              <button
                onClick={() => navigate('/auth')}
                className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-10 py-4 rounded-full text-lg font-semibold hover:from-teal-600 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Begin Your Journey Today
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Crisis Support Section */}
      <section id="crisis" className="py-16 bg-gradient-to-r from-red-50 to-orange-50 border-y border-red-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Need Immediate Support?</h2>
          <p className="text-lg text-slate-700 mb-8">
            If you're experiencing a mental health crisis, please reach out for immediate professional help.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 justify-center">
            <a 
              href="tel:1(246)536-4500" 
              className="bg-red-600 text-white px-6 py-4 rounded-full text-lg font-semibold hover:bg-red-700 transition-colors duration-200 shadow-lg"
              aria-label="Call Lifeline Barbados"
            >
              Call Lifeline Barbados
              <div className="text-sm opacity-90">1(246)536-4500</div>
            </a>
            <a 
              href="sms:741741" 
              className="border-2 border-red-500 text-red-600 px-6 py-4 rounded-full text-lg font-semibold hover:bg-red-50 transition-colors duration-200"
              aria-label="Text HOME to 741741 for Crisis Text Line"
            >
              Crisis Text Line
              <div className="text-sm opacity-75">Text HOME to 741741</div>
            </a>
            <a 
              href="https://findahelpline.com/i/iasp"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-orange-500 text-orange-600 px-6 py-4 rounded-full text-lg font-semibold hover:bg-orange-50 transition-colors duration-200"
              aria-label="Find a Crisis Center"
            >
              Find a Crisis Center
              <div className="text-sm opacity-75">Global Resources</div>
            </a>
          </div>
        </div>
      </section>

      {/* Avatar Integration Section */}
      <section id="avatar" className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50/50 via-white to-orange-50/50"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-in">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-50 to-blue-50 text-teal-700 px-4 py-2 rounded-full mb-6 border border-teal-200">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">AI Companion</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Meet Your Personal AI Companion
              </h2>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Powered by Tavus technology, your AI avatar provides a human-like interaction experience.
                Engage in meaningful conversations that feel natural and supportive.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4 group">
                  <div className="w-10 h-10 bg-gradient-to-br from-teal-100 to-teal-200 rounded-full flex items-center justify-center mt-1 group-hover:scale-110 transition-transform">
                    <Heart className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-slate-900 font-medium">Personalized Support</p>
                    <p className="text-slate-600 text-sm">Responses tailored to your unique mental health journey</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 group">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mt-1 group-hover:scale-110 transition-transform">
                    <MessageCircle className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-slate-900 font-medium">Always Available</p>
                    <p className="text-slate-600 text-sm">24/7 support whenever you need it most</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 group">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full flex items-center justify-center mt-1 group-hover:scale-110 transition-transform">
                    <Brain className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-slate-900 font-medium">Continuous Learning</p>
                    <p className="text-slate-600 text-sm">Adapts to better understand your needs over time</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 group">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center mt-1 group-hover:scale-110 transition-transform">
                    <Shield className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-slate-900 font-medium">Crisis Support</p>
                    <p className="text-slate-600 text-sm">Immediate emotional support when you need it</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 group">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-100 to-amber-200 rounded-full flex items-center justify-center mt-1 group-hover:scale-110 transition-transform">
                    <Wind className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-slate-900 font-medium">Guided Practices</p>
                    <p className="text-slate-600 text-sm">Meditation and therapeutic exercises tailored for you</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => navigate('/auth')}
                  className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-teal-600 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Sign Up Now
                </button>
                <button
                  onClick={() => navigate('/how-it-works')}
                  className="border-2 border-teal-500 text-teal-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-teal-50 transition-all duration-300"
                >
                  Learn How It Works
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-200/30 to-orange-200/30 rounded-3xl blur-3xl"></div>

              <AvatarDemo />

              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center shadow-md">
                  <Smile className="w-6 h-6 text-teal-500 mx-auto mb-2" />
                  <p className="text-xs text-slate-600 font-medium">Empathetic</p>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center shadow-md">
                  <Shield className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                  <p className="text-xs text-slate-600 font-medium">Secure</p>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center shadow-md">
                  <Sparkles className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                  <p className="text-xs text-slate-600 font-medium">Intelligent</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Journal Section */}
      <section id="journal" className="py-20 bg-gradient-to-br from-orange-50 via-amber-50 to-teal-50 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-2 h-2 bg-orange-300 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-20 w-3 h-3 bg-teal-300 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute bottom-20 left-1/3 w-2 h-2 bg-amber-300 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 animate-in">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-50 to-teal-50 text-orange-700 px-4 py-2 rounded-full mb-4 border border-orange-200">
              <Mic className="w-4 h-4" />
              <span className="text-sm font-medium">Express Yourself</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Voice-Powered Journaling
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Transform your thoughts into insights with AI-powered voice journaling that understands and responds to your emotional needs.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-card hover:shadow-card-hover ring-1 ring-orange-200/50 transition-all duration-300 border border-orange-100 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-br from-orange-100/30 to-transparent rounded-full -ml-32 -mt-32"></div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-gradient-to-tl from-teal-100/30 to-transparent rounded-full -mr-32 -mb-32"></div>

            <div className="grid lg:grid-cols-3 gap-8 items-center relative z-10">
              <div className="lg:col-span-2">
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Speak Your Mind, Find Your Peace</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Our advanced AI listens to not just your words, but the emotions behind them.
                  Get personalized insights, mood tracking, and gentle guidance on your wellness journey.
                </p>

                <div className="space-y-3 mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                      <Brain className="w-4 h-4 text-orange-600" />
                    </div>
                    <span className="text-slate-700">AI-powered emotional intelligence</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-teal-600" />
                    </div>
                    <span className="text-slate-700">Track your mood patterns over time</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-amber-600" />
                    </div>
                    <span className="text-slate-700">Personalized wellness insights</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={() => navigate('/auth')}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center gap-3"
                  >
                    <Mic className="w-6 h-6" />
                    Sign Up Now
                  </button>
                  <button
                    onClick={() => navigate('/how-it-works')}
                    className="border-2 border-orange-500 text-orange-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-orange-50 transition-all duration-300"
                  >
                    Learn How It Works
                  </button>
                </div>
              </div>
              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-200 to-teal-200 rounded-full blur-2xl opacity-50 animate-pulse"></div>
                  <div className="relative w-48 h-48 bg-gradient-to-br from-orange-100 to-teal-100 rounded-full shadow-2xl flex items-center justify-center">
                    <div className="absolute inset-4 border-2 border-dashed border-orange-300 rounded-full animate-spin" style={{ animationDuration: '20s' }}></div>
                    <Mic className="w-24 h-24 text-orange-500" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-500 rounded-xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-teal-400 to-orange-400 bg-clip-text text-transparent">
                Mira
              </span>
            </div>
            
            <div className="flex items-center gap-8">
              <nav className="flex gap-6">
                <button onClick={() => navigate('/privacy')} className="text-slate-300 hover:text-white transition-colors duration-200">Privacy</button>
                <button onClick={() => navigate('/terms')} className="text-slate-300 hover:text-white transition-colors duration-200">Terms</button>
                <button onClick={() => navigate('/support')} className="text-slate-300 hover:text-white transition-colors duration-200">Support</button>
                <button onClick={() => navigate('/about-us')} className="text-slate-300 hover:text-white transition-colors duration-200">About Us</button>
                <button onClick={() => navigate('/how-it-works')} className="text-slate-300 hover:text-white transition-colors duration-200">How It Works</button>
              </nav>
            </div>
          </div>
          
          <div className="border-t border-slate-700 mt-8 pt-8 text-center">
            <p className="text-slate-400">
              © 2025 Mira. Empathetic AI for mental wellness. Your journey matters.
            </p>
            <p className="text-slate-500 text-sm mt-2">
              Not a replacement for professional mental health services. If you're in crisis, please seek immediate help.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const Video = (props: any) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    {...props}
  >
    <path d="M22 8.13v7.74c0 .67-.5 1.2-1.14.96l-4.47-1.7c-.41-.16-.72-.56-.72-1.03V8.9c0-.47.31-.87.72-1.03l4.47-1.7c.64-.24 1.14.29 1.14.96Z" />
    <rect width="14" height="14" x="1" y="5" rx="2" ry="2" />
  </svg>
);

export default LandingPage;